import { Injectable, inject } from '@angular/core';
import { RollbarService } from '@becksdevteam/rollbar-angular';
import flagsmith from '@flagsmith/flagsmith';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { forkJoin, type Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserType } from '../../shared/models/user.interface';
import { UserService } from '../../shared/services/user.service';
import { UserActions } from '../actions/user.action';

@Injectable()
export class UserEffects {
  private _actions$ = inject(Actions);
  private _userService = inject(UserService);
  private _rollbarService = inject(RollbarService);

  userLoaded$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadUser),
      switchMap(() =>
        forkJoin([this._userService.fetchUser(), this._userService.fetchUserAccountStatus()]).pipe(
          switchMap(([user, accountStatus]) =>
            this._userService.fetchUserSecurity(user.id as number).pipe(
              map((security) => {
                return UserActions.loadUserSuccess({
                  user: {
                    ...user,
                    activeCustID: security.defaultCustID ?? 14,
                    activeCustBeckType: security.defaultBeckType,
                    salesRepDlrID: security.salesRepDlrID,
                  },
                  security,
                  accountStatus: accountStatus.status,
                });
              }),
              catchError((error) => {
                return of(UserActions.loadUserFail({ error }));
              })
            )
          )
        )
      )
    )
  );

  setFlagsmithLoggedInUser$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.loadUserSuccess),
        tap(({ user }) => {
          flagsmith.identify(user.email, {
            email: user.email,
            username: user.userName,
            id: user.id ?? null,
            type: user.type === UserType.AD_INTERNAL ? 'internal' : 'external',
          });
        })
      ),
    { dispatch: false }
  );

  setRollbarLoggedInUser$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.loadUserSuccess),
        tap(({ user }) => {
          this._rollbarService.configure({
            payload: {
              person: {
                id: user.id ?? null,
                username: user.userName,
                email: user.email,
              },
            },
          });
        })
      ),
    { dispatch: false }
  );
}
